
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TimeRangeSelector } from '@/components/analytics/TimeRangeSelector';
import { Loader2, BarChart3, FileText, Activity, ArrowUp, ArrowDown, Globe, Award } from 'lucide-react';
import { useBlogAnalytics } from '@/hooks/blog/useBlogAnalytics';
import { useProductAnalytics } from '@/hooks/product/useProductAnalytics';
import { useSiteAnalytics } from '@/hooks/analytics/useSiteAnalytics';
import { useAffiliateAnalytics } from '@/hooks/affiliate/useAffiliateAnalytics';
import ProductAnalytics from './ProductAnalytics';
import BlogAnalytics from './BlogAnalytics';
import SiteAnalytics from './SiteAnalytics';
import AffiliateAnalytics from './AffiliateAnalytics';
import { Helmet } from 'react-helmet';
import type { TimeRangeOption } from '@/types/analytics';

const UnifiedAnalytics = () => {
  const [timeRange, setTimeRange] = useState<TimeRangeOption>('7days');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Use the existing analytics hooks with the shared time range
  const { 
    loading: productsLoading, 
    totalClicks,
    clicksByDate,
    clicksByCategory,
    clicksByProduct 
  } = useProductAnalytics(timeRange);
  
  // Map TimeRangeOption to blog analytics accepted values
  const getBlogTimeRange = (range: TimeRangeOption): '7days' | '30days' | '90days' => {
    switch (range) {
      case '24h':
        return '7days'; // Fallback to 7days for 24h
      case '7days':
        return '7days';
      case '30days':
        return '30days';
      case '90days':
        return '90days';
      default:
        return '7days';
    }
  };
  
  const { 
    data: blogData, 
    loading: blogLoading,
  } = useBlogAnalytics(getBlogTimeRange(timeRange));

  const { 
    data: siteData, 
    isLoading: siteLoading 
  } = useSiteAnalytics(timeRange);

  const {
    loading: affiliateLoading,
    totalClicks: affiliateClicks,
    clicksGrowth: affiliateClicksGrowth,
  } = useAffiliateAnalytics(timeRange);
  
  const isLoading = productsLoading || blogLoading || siteLoading || affiliateLoading;
  
  // Calculate growth indicators (simplified example)
  const calculateGrowth = (current: number, previous: number): { value: number, isPositive: boolean } => {
    if (previous === 0) return { value: 100, isPositive: true };
    const growthValue = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(Math.round(growthValue)),
      isPositive: growthValue >= 0
    };
  };
  
  // Sample growth calculations based on available data
  const productClicksGrowth = calculateGrowth(
    totalClicks, 
    totalClicks * 0.8
  );

  const siteVisitsGrowth = siteData ? calculateGrowth(
    siteData.totalVisits,
    siteData.totalVisits * 0.85
  ) : { value: 0, isPositive: true };
  
  return (
    <div className="space-y-6">
      <Helmet>
        <title>Analytics Dashboard | Trotting the World</title>
      </Helmet>
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
          <p className="text-muted-foreground">Unified view of site traffic, product clicks, and blog performance metrics.</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="site">Site Analytics</TabsTrigger>
          <TabsTrigger value="products">Product Analytics</TabsTrigger>
          <TabsTrigger value="affiliates">Affiliate Analytics</TabsTrigger>
          <TabsTrigger value="blogs">Blog Analytics</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="ml-2 text-muted-foreground">Loading analytics data...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6 mb-8">
                {/* Site Visits Card */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Site Visits</CardTitle>
                    <Globe className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {siteData ? siteData.totalVisits.toLocaleString() : 0}
                    </div>
                    <div className="flex items-center pt-1 text-xs">
                      {siteVisitsGrowth.isPositive ? (
                        <ArrowUp className="h-3 w-3 text-emerald-500" />
                      ) : (
                        <ArrowDown className="h-3 w-3 text-rose-500" />
                      )}
                      <span className={siteVisitsGrowth.isPositive ? "text-emerald-500" : "text-rose-500"}>
                        {siteVisitsGrowth.value}%
                      </span>
                      <span className="text-muted-foreground ml-1">vs previous period</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Total Product Clicks Card */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Product Clicks</CardTitle>
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{totalClicks}</div>
                    <div className="flex items-center pt-1 text-xs">
                      {productClicksGrowth.isPositive ? (
                        <ArrowUp className="h-3 w-3 text-emerald-500" />
                      ) : (
                        <ArrowDown className="h-3 w-3 text-rose-500" />
                      )}
                      <span className={productClicksGrowth.isPositive ? "text-emerald-500" : "text-rose-500"}>
                        {productClicksGrowth.value}%
                      </span>
                      <span className="text-muted-foreground ml-1">vs previous period</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Affiliate Clicks Card */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Affiliate Clicks</CardTitle>
                    <Award className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{affiliateClicks.toLocaleString()}</div>
                    <div className="flex items-center pt-1 text-xs">
                      {affiliateClicksGrowth >= 0 ? (
                        <ArrowUp className="h-3 w-3 text-emerald-500" />
                      ) : (
                        <ArrowDown className="h-3 w-3 text-rose-500" />
                      )}
                      <span className={affiliateClicksGrowth >= 0 ? "text-emerald-500" : "text-rose-500"}>
                        {Math.abs(affiliateClicksGrowth).toFixed(1)}%
                      </span>
                      <span className="text-muted-foreground ml-1">vs previous period</span>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Total Blog Views Card */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Blog Views</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {blogData ? blogData.totalViews.toLocaleString() : 0}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      All-time cumulative
                    </p>
                  </CardContent>
                </Card>
                
                {/* Unique Visitors Card */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Unique Visitors</CardTitle>
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {siteData ? siteData.uniqueVisitors.toLocaleString() : 0}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Individual users
                    </p>
                  </CardContent>
                </Card>
                
                {/* Live Visitors Card */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Live Visitors</CardTitle>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <Activity className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {siteData ? siteData.realTimeVisitors : 0}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Active now
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Top Pages Card */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top Pages</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {siteData ? siteData.topPages.slice(0, 5).map((page, index) => (
                        <div key={page.page_path} className="flex items-center justify-between border-b pb-2 last:border-0">
                          <span className="font-medium truncate max-w-[250px]">
                            {page.page_title || page.page_path}
                          </span>
                          <span className="text-sm font-semibold tabular-nums">
                            {page.page_views} views
                          </span>
                        </div>
                      )) : (
                        <div className="text-center py-4 text-muted-foreground">No site data available</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Top Products Card */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top Products</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {clicksByProduct.slice(0, 5).map((product, index) => (
                        <div key={index} className="flex items-center justify-between border-b pb-2 last:border-0">
                          <span className="font-medium truncate max-w-[250px]">{product.product_name}</span>
                          <span className="text-sm font-semibold tabular-nums">
                            {product.clicks} clicks
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>
        
        <TabsContent value="site">
          <SiteAnalytics />
        </TabsContent>
        
        <TabsContent value="products">
          <ProductAnalytics />
        </TabsContent>

        <TabsContent value="affiliates">
          <AffiliateAnalytics />
        </TabsContent>
        
        <TabsContent value="blogs">
          <BlogAnalytics />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UnifiedAnalytics;
